import { forwardRef } from "react";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import type { VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { menuItemVariants } from "./Menu";

/**
 * ContextMenu — right-click menu on @radix-ui/react-context-menu. Hosts
 * the portal ledger's row menu.
 *
 *   <ContextMenu>
 *     <ContextMenuTrigger asChild>
 *       <LedgerRow>…</LedgerRow>
 *     </ContextMenuTrigger>
 *     <ContextMenuContent>
 *       <ContextMenuItem onSelect={open}>Open row</ContextMenuItem>
 *       <ContextMenuItem tone="destructive">Delete row</ContextMenuItem>
 *     </ContextMenuContent>
 *   </ContextMenu>
 *
 * Items share the Menu item styling (menuItemVariants) so dropdown and
 * right-click menus can never drift apart visually.
 */

export interface ContextMenuProps
  extends React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Root> {}

/** Controlled/uncontrolled root. Renders no DOM element. */
export function ContextMenu(props: ContextMenuProps) {
  return <ContextMenuPrimitive.Root {...props} />;
}
ContextMenu.displayName = "ContextMenu";

export interface ContextMenuTriggerProps
  extends React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Trigger> {}

/** Wraps the right-click surface — always use `asChild` on the row itself. */
export const ContextMenuTrigger = forwardRef<
  React.ComponentRef<typeof ContextMenuPrimitive.Trigger>,
  ContextMenuTriggerProps
>(({ className, ...props }, ref) => {
  return (
    <ContextMenuPrimitive.Trigger ref={ref} className={className} {...props} />
  );
});
ContextMenuTrigger.displayName = "ContextMenuTrigger";

export interface ContextMenuContentProps
  extends React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content> {}

/** Portalled raised panel — identical grammar to MenuContent. Anchors at
    the pointer, so there is no side offset to configure. */
export const ContextMenuContent = forwardRef<
  React.ComponentRef<typeof ContextMenuPrimitive.Content>,
  ContextMenuContentProps
>(({ className, ...props }, ref) => {
  return (
    <ContextMenuPrimitive.Portal>
      <ContextMenuPrimitive.Content
        ref={ref}
        className={cn(
          "z-(--z-popover) min-w-44 max-h-96 overflow-y-auto rounded-lg border border-border-default bg-surface-raised p-1 shadow-menu outline-none",
          className,
        )}
        {...props}
      />
    </ContextMenuPrimitive.Portal>
  );
});
ContextMenuContent.displayName = "ContextMenuContent";

export interface ContextMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Item>,
    VariantProps<typeof menuItemVariants> {}

export const ContextMenuItem = forwardRef<
  React.ComponentRef<typeof ContextMenuPrimitive.Item>,
  ContextMenuItemProps
>(({ className, tone, ...props }, ref) => {
  return (
    <ContextMenuPrimitive.Item
      ref={ref}
      className={cn(
        menuItemVariants({ tone }),
        "data-disabled:pointer-events-none data-disabled:text-content-disabled",
        className,
      )}
      {...props}
    />
  );
});
ContextMenuItem.displayName = "ContextMenuItem";
